import React, { useState } from 'react';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { getPatientByNhs } from '../../../actions/entities/patients';
import LoadingPageText from '../../../components/common/Loading/LoadingPageText';
import ConfirmationDialog from '../../../components/PageStructure/ConfirmationDialog';
import { parseShortDateTime } from '../../../utils/dates';
import { ValidationCodes } from '../../../enums/ValidationCodes';
import validationRegister from '../../../validation/validationRegister';

export default function ImportPatientForm({ events }) {
  const [state, setState] = useState({
    nhsNumber: '',
    nhsNumberErrorMessage: null,
    selectedPatient: null,
    errorMsg: '',
    isFeaching: false,
    showConfirmationDialog: false,
    onSearchActivated: false,
  });

  const searchPatientByNhs = () => {
    setState({ ...state, onSearchActivated: true });
    if (state.nhsNumberErrorMessage === null) {
      getPatientByNhs(events.auth, state.nhsNumber, response =>
        setState({
          ...state,
          selectedPatient: response.patient,
          errorMsg: response.errorMessage,
          isFeaching: false,
          nhsNumberErrorMessage: null,
          onSearchActivated: false,
        }),
      );
    }
  };

  const formattedErrorMsg = () => {
    return state.errorMsg.split(';').map(item => <p>{item}</p>);
  };

  const errorPlaceholder = () => {
    if (state.errorMsg === '') return null;
    return (
      <span className="form-validation-error">
        <br />
        {formattedErrorMsg()}
      </span>
    );
  };

  const resultPatientRender = () => {
    if (state.selectedPatient === null) return null;

    return (
      <div className="clinician-info-box" style={{ marginTop: '30px' }}>
        <Typography variant="h6" gutterBottom>
          Patient details
        </Typography>
        <List className="list-underline">
          <ListItem>
            <ListItemText primary="First and Last Name" />
            <span className="divider" />
            <ListItemText secondary={state.selectedPatient.patientFullName || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="NHS No" />
            <span className="divider" />
            <ListItemText secondary={state.selectedPatient.nhsNumber || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Date of birth" />
            <span className="divider" />
            <ListItemText secondary={parseShortDateTime(state.selectedPatient.dateOfBirth)} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Gender" />
            <span className="divider" />
            <ListItemText secondary={state.selectedPatient.gender || '-'} />
          </ListItem>
        </List>
      </div>
    );
  };

  const importPatientByNhs = () => {
    if (state.selectedPatient != null) {
      events.importPatientEvent({ nhsNumber: state.selectedPatient.nhsNumber }, response =>
        setState({
          ...state,
          errorMsg: response.message,
          isFeaching: false,
          showConfirmationDialog: true,
          nhsNumberErrorMessage: null,
        }),
      );
      setState({ ...state, isFeaching: true });
    }
  };

  const confirmImportPatientByNhs = () => {
    if (state.selectedPatient != null) {
      events.importPatientEvent({
        nhsNumber: state.selectedPatient.nhsNumber,
        overwritePatient: true,
        nhsNumberErrorMessage: null,
      });
      setState({ ...state, isFeaching: true });
    }
  };

  const handleNhsNumberChange = event => {
    const { nhsNumber, nhsNumberErrorMessage } = validationRegister.executeValidationModule(
      ValidationCodes.NHS,
      event.target.value,
    );
    setState({ ...state, nhsNumber, nhsNumberErrorMessage, onSearchActivated: false });
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer anchor="right" open onOpen={() => {}} onClose={() => {}}>
        <div className="sidePanel-wrapper" style={{ width: '400px' }}>
          <div className="sidePanel-header form-header">
            <div className="sidePanel-header-subtitle">
              <Box display="flex">
                <Box flexGrow={1} py={1}>
                  <Typography variant="subtitle1">Import patient</Typography>
                </Box>
                <Box>
                  <IconButton onClick={() => events.handleImportEvent(false)}>
                    <CancelIcon />
                  </IconButton>
                </Box>
              </Box>
            </div>
          </div>
          <div className="sidePanel-content content-top-padding">
            <Typography variant="h6" gutterBottom>
              Find Patient by NHS number
            </Typography>
            <TextField
              required
              style={{ width: '90%' }}
              disabled={state.isFeaching}
              onChange={handleNhsNumberChange}
              value={state.nhsNumber}
              error={state.onSearchActivated && state.nhsNumberErrorMessage !== null}
              helperText={state.onSearchActivated && state.nhsNumberErrorMessage}
            />
            <IconButton
              type="submit"
              size="small"
              onClick={searchPatientByNhs}
              className="search-icon"
              disabled={state.isFeaching || !state.nhsNumber}
            >
              <SearchIcon />
            </IconButton>
            {state.isFeaching && (
              <LoadingPageText showHeader={false}>Wait a moment while we gather patient info...</LoadingPageText>
            )}
            {errorPlaceholder()}
            {resultPatientRender()}
          </div>
          <div className="sidePanel-footer">
            <div className="sidePanel-buttons">
              <div className="sidePanel-BackButton">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => events.handleImportEvent(false)}
                  style={{ marginRight: '10px' }}
                  disabled={state.isFeaching}
                >
                  Back
                </Button>
              </div>
              <div className="sidePanel-saveButton">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={state.selectedPatient === null || state.isFeaching}
                  onClick={importPatientByNhs}
                >
                  Import Patient
                </Button>
              </div>
            </div>
          </div>
        </div>
        {state.errorMsg === 'The patient already exists. Are you sure you want to overwrite the existing patient?' && (
          <ConfirmationDialog
            isOpen={state.showConfirmationDialog}
            confirmationTitle={'Overwrite Patient'}
            confirmationDesc={state.errorMsg}
            onConfirm={confirmImportPatientByNhs}
            onClose={() => setState({ ...state, showConfirmationDialog: false })}
          />
        )}
      </SwipeableDrawer>
    </section>
  );
}
